import {Component, OnInit} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';
import {EventResponse, EventTypeResponse, CompanyResponse, EventClassResponse, EventServiceResponse, TrainingPackageResponse} from '../../@shared/models/response.module';
import {EventsService} from '../../@shared/services/events-service';
import {TrainingService} from '../../@shared/services/training.service';
import {AQH_EVE_0, AQH_EVE_4, AQH_EVE_6, AQH_EVE_11, COR_COY_4} from '../../@shared/models/input.module';
import {formatDateDMYDots} from '../../@core/utils/utils.service';
import {CompanyService} from '../../@shared/services/company.service';
import {GeocodingService} from '../../@shared/services/geocoding.service';
import {MapMarker, MapConfig} from '../../@shared/components/location-map/location-map';
import {TranslateService} from '@ngx-translate/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AuthService} from '../../@core/services/auth.service';
import {CreateEventOrgSelectorModal} from '../../@shared/components/create-event-org-selector-modal/create-event-org-selector-modal';

@Component({
  selector: 'app-clinics',
  standalone: false,
  templateUrl: './clinics.html',
  styleUrl: './clinics.css'
})
export class Clinics implements OnInit {
  events: EventResponse[] = [];
  filteredEvents: EventResponse[] = [];
  upcomingEvents: EventResponse[] = [];
  pastEvents: EventResponse[] = [];
  eventTypes: EventTypeResponse[] = [];
  clinicEventTypes: EventTypeResponse[] = []; // Only clinic/training types
  companies: CompanyResponse[] = [];

  // Filters
  searchQuery: string = '';
  selectedCategory: 'training' | 'social' | null = null;
  selectedCompanyId: string | null = null;
  selectedDiscipline: string | null = null;
  sortByDistance: boolean = false;
  dateFrom: string | null = null;
  dateTo: string | null = null;

  // Category options for filter (will be populated with translations)
  categoryOptions: { id: string; name: string }[] = [];

  disciplineOptions = [
    { value: 'distance', label: 'Distanse' },
    { value: 'dressage', label: 'Dressur' },
    { value: 'eventing', label: 'Feltritt' },
    { value: 'icelandic', label: 'Islandshest' },
    { value: 'driving', label: 'Kjøring' },
    { value: 'mounted_games', label: 'Mounted Games' },
    { value: 'para_dressage', label: 'Paradressur' },
    { value: 'jumping', label: 'Sprang' },
    { value: 'vaulting', label: 'Voltige' },
    { value: 'western', label: 'Western' },
    { value: 'barrel_racing', label: 'Barrel Racing' },
    { value: 'pole_bending', label: 'Pole Bending' },
    { value: 'reining', label: 'Reining' },
    { value: 'working_eq', label: 'Working Equitation' },
    { value: 'skeid', label: 'Skeid' },
    { value: 'equestrian', label: 'Riddesport' },
    { value: 'trotting', label: 'Trav' },
    { value: 'gallop', label: 'Galopp' },
    { value: 'polo', label: 'Polo' },
    { value: 'academic', label: 'Akademisk Ridekunst' },
    { value: 'exhibition', label: 'Utstilling' },
    { value: 'struk', label: 'STRUK' },
    { value: 'other', label: 'Annet' }
  ];

  // User location
  userLocation: { lat: number; lng: number } | null = null;
  locationLoading: boolean = false;
  locationError: string | null = null;

  // Event coordinates cache (from geocoding)
  eventCoordinates: { [eventId: number]: { lat: number; lng: number } } = {};

  // Loading states
  loading: boolean = false;

  // Event classes, services, and packages data (keyed by event_id)
  eventClasses: { [eventId: number]: EventClassResponse[] } = {};
  eventServices: { [eventId: number]: EventServiceResponse[] } = {};
  eventPackages: { [eventId: number]: TrainingPackageResponse[] } = {};
  loadingClasses: { [eventId: number]: boolean } = {};
  loadingServices: { [eventId: number]: boolean } = {};
  loadingPackages: { [eventId: number]: boolean } = {};

  // Map properties
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: '400px',
    mobileHeight: '300px',
    enableClustering: true,
    defaultCenter: { lat: 59.9139, lng: 10.7522 }, // Oslo, Norway
    defaultZoom: 5
  };
  geocodingInProgress: boolean = false;

  constructor(
    private eventsService: EventsService,
    private companyService: CompanyService,
    private geocodingService: GeocodingService,
    private trainingService: TrainingService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private authService: AuthService
  ) {}

  private readFiltersFromUrl() {
    const params = this.route.snapshot.queryParams;
    if (params['search']) this.searchQuery = params['search'];
    if (params['category']) this.selectedCategory = params['category'] as 'training' | 'social';
    if (params['company']) this.selectedCompanyId = params['company'];
    if (params['discipline']) this.selectedDiscipline = params['discipline'];
    if (params['dateFrom']) this.dateFrom = params['dateFrom'];
    if (params['dateTo']) this.dateTo = params['dateTo'];
    if (params['sortByDistance'] === 'true') this.sortByDistance = true;
  }

  private updateUrlWithFilters() {
    const queryParams: any = {};
    if (this.searchQuery) queryParams.search = this.searchQuery;
    if (this.selectedCategory) queryParams.category = this.selectedCategory;
    if (this.selectedCompanyId) queryParams.company = this.selectedCompanyId;
    if (this.selectedDiscipline) queryParams.discipline = this.selectedDiscipline;
    if (this.dateFrom) queryParams.dateFrom = this.dateFrom;
    if (this.dateTo) queryParams.dateTo = this.dateTo;
    if (this.sortByDistance) queryParams.sortByDistance = 'true';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  ngOnInit() {
    this.readFiltersFromUrl();
    this.initCategoryOptions();
    this.loadEventTypes();
    this.loadCompanies();
    this.getUserLocation();

    // Re-initialize category options when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.initCategoryOptions();
    });
  }

  private initCategoryOptions() {
    this.categoryOptions = [
      { id: 'training', name: this.translateService.instant('trainings.categoryTraining') },
      { id: 'social', name: this.translateService.instant('trainings.categorySocial') }
    ];
  }

  loadEvents() {
    this.loading = true;
    const params: AQH_EVE_0 = {
      compact: true  // Use compact mode for ~70% smaller payload
    };

    // Use public endpoint (no auth required) with compact mode
    this.eventsService.getPublicEvents(params).subscribe({
      next: (response) => {
        // Filter for training and social events
        // Use category if set, otherwise fall back to event_type_id for legacy events
        this.events = (response.data || []).filter(event =>
          event.category === 'training' ||
          event.category === 'social' ||
          (!event.category && (event.event_type_id === 1 || event.event_type_id === 2))
        );
        this.sortEventsByDate();
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadEventTypes() {
    // Use public endpoint (no auth required)
    this.eventsService.getPublicEventTypes().subscribe({
      next: (types) => {
        this.eventTypes = types || [];
        // Filter to only clinic/training types for the dropdown
        this.clinicEventTypes = this.eventTypes.filter(type => {
          const name = type.event_type_name?.toLowerCase() || '';
          return name.includes('clinic') || name.includes('training');
        });
        // Now load events after we have the event types
        this.loadEvents();
      }
    });
  }

  loadCompanies() {
    const params: COR_COY_4 = {};
    // Use public endpoint (no auth required)
    this.companyService.getPublicCompanies(params).subscribe({
      next: (response) => {
        this.companies = response.data || [];
      }
    });
  }

  sortEventsByDate() {
    this.events.sort((a, b) => {
      const dateA = a.start_date ? new Date(a.start_date) : new Date(8640000000000000); // Max date if no start_date
      const dateB = b.start_date ? new Date(b.start_date) : new Date(8640000000000000);

      // Sort by earliest date first (ascending order)
      return dateA.getTime() - dateB.getTime();
    });
  }

  applyFilters() {
    this.filteredEvents = this.events.filter(event => {
      // Search filter (event name, description, city)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch =
          event.event_name?.toLowerCase().includes(query) ||
          event.event_description?.toLowerCase().includes(query) ||
          event.city?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter (training or social)
      if (this.selectedCategory) {
        const eventCategory = event.category || (event.event_type_id === 2 ? 'training' : event.event_type_id === 1 ? 'social' : null);
        if (eventCategory !== this.selectedCategory) return false;
      }

      // Company filter
      if (this.selectedCompanyId) {
        if (event.company_id !== this.selectedCompanyId) return false;
      }

      // Discipline filter - treat null/undefined as 'other'
      if (this.selectedDiscipline) {
        const eventDiscipline = event.discipline || 'other';
        if (eventDiscipline !== this.selectedDiscipline) return false;
      }

      // Date range filter
      if (this.dateFrom || this.dateTo) {
        const eventStartDate = event.start_date ? new Date(event.start_date) : null;
        const eventEndDate = event.end_date ? new Date(event.end_date) : eventStartDate;

        if (!eventStartDate) return false;

        if (this.dateFrom) {
          const fromDate = new Date(this.dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          // Event ends before the filter start date
          if (eventEndDate && eventEndDate < fromDate) return false;
          if (!eventEndDate && eventStartDate < fromDate) return false;
        }

        if (this.dateTo) {
          const toDate = new Date(this.dateTo);
          toDate.setHours(23, 59, 59, 999);
          // Event starts after the filter end date
          if (eventStartDate > toDate) return false;
        }
      }

      return true;
    });

    // Separate into upcoming and past events
    this.upcomingEvents = this.filteredEvents.filter(event => this.isEventUpcoming(event.start_date));
    this.pastEvents = this.filteredEvents.filter(event => this.isEventPast(event.start_date));

    // Geocode events for map
    this.geocodeEvents();
  }

  async geocodeEvents() {
    this.geocodingInProgress = true;
    this.mapMarkers = [];

    const eventsToGeocode = this.filteredEvents.filter(
      event => event.address || event.city || event.country
    );

    // Debug: log first event to check address fields
    if (eventsToGeocode.length > 0) {
      const first = eventsToGeocode[0];
      console.log('[Geocode Debug] First clinic address fields:', {
        event_name: first.event_name,
        address: first.address,
        postal_code: first.postal_code,
        city: first.city,
        country: first.country
      });
    }

    for (const event of eventsToGeocode) {
      const result = await this.geocodingService.geocodeAddress(
        event.address || '',
        event.city || '',
        event.postal_code || '',
        event.country || ''
      );

      if (result.success && result.lat && result.lng) {
        // Cache coordinates for distance calculation
        this.eventCoordinates[event.event_id] = { lat: result.lat, lng: result.lng };

        this.mapMarkers = [...this.mapMarkers, {
          id: event.event_id,
          lat: result.lat,
          lng: result.lng,
          title: event.event_name || 'Unknown Event',
          subtitle: this.formatEventSubtitle(event),
          imageUrl: event.header_url || undefined,
          color: this.getMarkerColor(event),
          data: event
        }];
      }
    }

    this.geocodingInProgress = false;

    // Re-sort if sorting by distance is enabled
    if (this.sortByDistance && this.userLocation) {
      this.sortEventsByDistance();
    }
  }

  private sortEventsByDistance() {
    if (!this.userLocation) return;

    const sortByDist = (a: EventResponse, b: EventResponse) => {
      const distA = this.getEventDistance(a);
      const distB = this.getEventDistance(b);
      if (distA === null && distB === null) return 0;
      if (distA === null) return 1;
      if (distB === null) return -1;
      return distA - distB;
    };

    this.upcomingEvents.sort(sortByDist);
    this.pastEvents.sort(sortByDist);
  }

  private formatEventSubtitle(event: EventResponse): string {
    const parts = [];
    if (event.city) parts.push(event.city);
    if (event.start_date) parts.push(this.formatDateDMYDots(event.start_date));
    return parts.join(' | ');
  }

  onMapMarkerClick(marker: MapMarker) {
    const element = document.getElementById(`event-card-${marker.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight');
      setTimeout(() => element.classList.remove('highlight'), 2000);
    }
  }

  onMapInfoWindowAction(marker: MapMarker) {
    this.router.navigate(['/event-details', marker.id]);
  }

  onSearchChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  onEventTypeChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  onCategoryChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  onCompanyChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedCategory = null;
    this.selectedCompanyId = null;
    this.selectedDiscipline = null;
    this.sortByDistance = false;
    this.dateFrom = null;
    this.dateTo = null;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  onDisciplineChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  onDateFilterChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getDisciplineLabel(value: string | undefined): string {
    if (!value) return this.translateService.instant('trainings.allCategories');
    const discipline = this.disciplineOptions.find(d => d.value === value);
    return discipline?.label || value;
  }

  toggleSortByDistance() {
    this.sortByDistance = !this.sortByDistance;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    this.locationLoading = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.locationLoading = false;
      },
      () => {
        this.locationLoading = false;
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  // Haversine formula to calculate distance between two points
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getEventDistance(event: EventResponse): number | null {
    if (!this.userLocation) return null;
    const coords = this.eventCoordinates[event.event_id];
    if (!coords) return null;
    return this.calculateDistance(
      this.userLocation.lat,
      this.userLocation.lng,
      coords.lat,
      coords.lng
    );
  }

  formatDistance(km: number | null): string {
    if (km === null) return '';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${Math.round(km)} km`;
  }

  getCategoryDisplay(event: EventResponse): string {
    // Use category if available, otherwise fall back to event type
    if (event.category) {
      switch (event.category) {
        case 'competition': return this.translateService.instant('event.category.competition');
        case 'training': return this.translateService.instant('event.category.training');
        case 'social': return this.translateService.instant('event.category.social');
        default: return this.translateService.instant('event.category.event');
      }
    }
    // Legacy fallback
    const eventType = this.eventTypes.find(t => t.event_type_id === event.event_type_id);
    return eventType?.event_type_name || this.translateService.instant('event.category.event');
  }

  getCategoryBadgeClass(event: EventResponse): string {
    // Use category if available
    if (event.category) {
      switch (event.category) {
        case 'competition': return 'badge-competition';
        case 'training': return 'badge-training';
        case 'social': return 'badge-social';
        default: return 'badge-secondary';
      }
    }
    // Legacy fallback based on event_type_id
    if (event.event_type_id === 0) return 'badge-competition';
    if (event.event_type_id === 2) return 'badge-training';
    if (event.event_type_id === 1) return 'badge-social';
    return 'badge-secondary';
  }

  getMarkerColor(event: EventResponse): 'orange' | 'blue' | 'purple' | 'red' {
    // Use category if available
    if (event.category) {
      switch (event.category) {
        case 'competition': return 'orange';
        case 'training': return 'blue';
        case 'social': return 'purple';
        default: return 'red';
      }
    }
    // Legacy fallback based on event_type_id
    if (event.event_type_id === 0) return 'orange';
    if (event.event_type_id === 2) return 'blue';
    if (event.event_type_id === 1) return 'purple';
    return 'red';
  }

  getEventTypeDisplay(eventTypeId: number): string {
    const eventType = this.eventTypes.find(t => t.event_type_id === eventTypeId);
    return eventType?.event_type_name || 'Event';
  }

  getEventTypeBadgeClass(eventTypeId: number): string {
    const eventType = this.eventTypes.find(t => t.event_type_id === eventTypeId);
    const typeName = eventType?.event_type_name?.toLowerCase() || '';

    if (typeName.includes('clinic')) {
      return 'badge-primary'; // Blue
    } else if (typeName.includes('training')) {
      return 'badge-info'; // Light Blue/Teal
    }

    return 'badge-secondary'; // Default gray
  }

  isEventUpcoming(startDate: string | Date | null | undefined): boolean {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(startDate);
    return eventDate >= today;
  }

  isEventPast(startDate: string | Date | null | undefined): boolean {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(startDate);
    return eventDate < today;
  }

  loadEventClasses(eventId: number) {
    // Only load if not already loaded or loading
    if (this.eventClasses[eventId] || this.loadingClasses[eventId]) {
      return;
    }

    this.loadingClasses[eventId] = true;
    const params: AQH_EVE_6 = {
      event_id: eventId
    };

    this.eventsService.getEventClasses(params).subscribe({
      next: (classes) => {
        this.eventClasses[eventId] = classes || [];
        this.loadingClasses[eventId] = false;
      },
      error: () => {
        this.loadingClasses[eventId] = false;
        this.eventClasses[eventId] = [];
      }
    });
  }

  loadEventServices(eventId: number) {
    // Only load if not already loaded or loading
    if (this.eventServices[eventId] || this.loadingServices[eventId]) {
      return;
    }

    this.loadingServices[eventId] = true;
    const params: AQH_EVE_11 = {
      event_id: eventId
    };

    this.eventsService.getEventServices(params).subscribe({
      next: (services) => {
        this.eventServices[eventId] = services || [];
        this.loadingServices[eventId] = false;
      },
      error: () => {
        this.loadingServices[eventId] = false;
        this.eventServices[eventId] = [];
      }
    });
  }

  loadEventPackages(eventId: number) {
    // Only load if not already loaded or loading
    if (this.eventPackages[eventId] || this.loadingPackages[eventId]) {
      return;
    }

    this.loadingPackages[eventId] = true;

    // Use getPackagesWithAvailability to get capacity-based availability
    this.trainingService.getPackagesWithAvailability({ event_id: eventId }).subscribe({
      next: (packages) => {
        this.eventPackages[eventId] = packages || [];
        this.loadingPackages[eventId] = false;
      },
      error: () => {
        this.loadingPackages[eventId] = false;
        this.eventPackages[eventId] = [];
      }
    });
  }

  getPackageAvailabilityBadge(pkg: TrainingPackageResponse): { class: string; text: string } {
    if (!pkg.is_available) {
      return { class: 'badge-light-danger', text: this.translateService.instant('event.packageFull') };
    } else if (pkg.slots_left != null && pkg.slots_left <= 3) {
      return { class: 'badge-light-warning', text: `${pkg.slots_left} ${this.translateService.instant('event.slotsLeft')}` };
    }
    return { class: 'badge-light-success', text: this.translateService.instant('event.available') };
  }

  formatPackagePrice(priceCents: number, currency: string): string {
    const price = priceCents / 100;
    const formatted = price % 1 === 0 ? price.toString() : price.toFixed(2);
    return `${formatted} ${currency}`;
  }

  isTrainingEvent(event: EventResponse): boolean {
    return event.category === 'training' || (!event.category && event.event_type_id === 2);
  }

  isSocialEvent(event: EventResponse): boolean {
    return event.category === 'social' || (!event.category && event.event_type_id === 1);
  }

  getGoogleMapsUrl(event: EventResponse): string {
    // Build the full address string
    const addressParts = [
      event.address,
      event.postal_code,
      event.city,
      event.country
    ].filter(part => part); // Remove empty parts

    const fullAddress = addressParts.join(', ');

    // Encode the address for URL
    const encodedAddress = encodeURIComponent(fullAddress);

    // Return Google Maps search URL
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  getServiceAvailableSlots(service: EventServiceResponse): number {
    if (!service.max_num_registrations) return 0;
    const registeredCount = service.event_service_registrations?.length || 0;
    return Math.max(0, service.max_num_registrations - registeredCount);
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;

  openCreateEvent() {
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }
    const modalRef = this.modalService.open(CreateEventOrgSelectorModal, { centered: true });
    modalRef.result.then((company: CompanyResponse) => {
      this.router.navigate(['/company-profile/create-event', company.company_id]);
    }).catch(() => {});
  }
}
